import React, { useState } from 'react';
import { Eye, CheckCircle, XCircle, Mail } from 'lucide-react';
import { format } from 'date-fns';

export default function ReservationsManagementTable({ reservations, selectedIds, onSelectIds, onSelectReservation, onUpdateStatus, onRefresh }) {
  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      onSelectIds(reservations?.map(r => r?.id));
    } else {
      onSelectIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds?.includes(id)) {
      onSelectIds(selectedIds?.filter(sid => sid !== id));
    } else {
      onSelectIds([...selectedIds, id]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'processing': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-purple-600 bg-purple-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'En Attente';
      case 'confirmed': return 'Confirmée';
      case 'processing': return 'En Traitement';
      case 'completed': return 'Terminée';
      case 'cancelled': return 'Annulée';
      default: return status;
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedIds?.length === reservations?.length && reservations?.length > 0}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collecte</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confirmation</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {reservations?.length === 0 ? (
            <tr>
              <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                Aucune réservation trouvée
              </td>
            </tr>
          ) : (
            reservations?.map((reservation) => (
              <tr key={reservation?.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds?.includes(reservation?.id)}
                    onChange={() => handleSelectOne(reservation?.id)}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {format(new Date(reservation?.created_at), 'dd/MM/yyyy')}
                  <div className="text-xs text-gray-500">{format(new Date(reservation?.created_at), 'HH:mm')}</div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {reservation?.customer_name}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  <div>{reservation?.customer_phone}</div>
                  {reservation?.customer_email && (
                    <div className="text-xs text-gray-500">{reservation?.customer_email}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {reservation?.service_type || 'Non spécifié'}
                  {reservation?.collection_date && (
                    <div className="text-xs text-gray-500">
                      {format(new Date(reservation?.collection_date), 'dd/MM/yyyy')}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  <div className="max-w-xs truncate">{reservation?.pickup_address}</div>
                  {reservation?.pickup_city && (
                    <div className="text-xs text-gray-500">{reservation?.pickup_city}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(reservation?.status)}`}>
                    {getStatusLabel(reservation?.status)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {reservation?.confirmation_sent ? (
                    <div className="flex items-center space-x-1 text-green-600">
                      <Mail size={16} />
                      <span className="text-xs">Envoyée</span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-500">Non envoyée</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onSelectReservation(reservation)}
                      className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                      title="Voir détails"
                    >
                      <Eye size={18} />
                    </button>
                    {reservation?.status === 'pending' && (
                      <>
                        <button
                          onClick={() => onUpdateStatus(reservation?.id, 'confirmed', null)}
                          className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                          title="Confirmer"
                        >
                          <CheckCircle size={18} />
                        </button>
                        <button
                          onClick={() => onUpdateStatus(reservation?.id, 'cancelled', 'Annulée par admin')}
                          className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Annuler"
                        >
                          <XCircle size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}